import { faClock, faHeartPulse, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import type { TFunction } from 'i18next';

import type {
  IVirtuousDownsyncActivities,
  IVirtuousIntegrationAttributes,
  IVirtuousSyncSummary,
  IVirtuousUpsyncActivities,
  VirtuousIntegration,
} from '@feathr/blackbox';
import type { IIntegrationUIStat, TIntegrationUIToggleValues } from '@feathr/components';
import {
  formatLastSynced,
  UNAVAILABLE_VALUE,
} from '@feathr/extender/App/Settings/Account/CrmIntegrationPage/crmSyncSummary.utils';

import {
  ACTIVITY_UPSYNC_KEY,
  CONTACT_DOWNSYNC_KEY,
  DONATION_UPSYNC_KEY,
  GIFT_DOWNSYNC_KEY,
  PERSON_UPSYNC_KEY,
} from './virtuousConfig';

type TUpsyncSwitch = 'activity' | 'donations' | 'people';

/** The sync toggles' values from the model. */
export function getActivityValues({
  downsync_activities: downsync,
  upsync_activities: upsync,
}: Partial<
  Pick<IVirtuousIntegrationAttributes, 'downsync_activities' | 'upsync_activities'>
>): TIntegrationUIToggleValues {
  return {
    [ACTIVITY_UPSYNC_KEY]: upsync?.activity ?? false,
    [CONTACT_DOWNSYNC_KEY]: downsync?.contacts ?? false,
    [DONATION_UPSYNC_KEY]: upsync?.donations ?? false,
    [GIFT_DOWNSYNC_KEY]: downsync?.gifts ?? false,
    [PERSON_UPSYNC_KEY]: upsync?.people ?? false,
  };
}

/** Stages one downsync switch on the model. The page's Save & Sync persists it. */
export function stageDownsyncActivity(
  integration: VirtuousIntegration,
  key: keyof IVirtuousDownsyncActivities,
  value: boolean,
): void {
  // A record created before its first save can lack the object.
  const current: Partial<IVirtuousDownsyncActivities> | undefined =
    integration.get('downsync_activities');
  integration.set({
    downsync_activities: { contacts: false, gifts: false, ...current, [key]: value },
  });
}

/** Stages one upsync switch on the model. Keeps the other upsync settings. */
export function stageUpsyncActivity(
  integration: VirtuousIntegration,
  key: TUpsyncSwitch,
  value: boolean,
): void {
  const current: Partial<IVirtuousUpsyncActivities> | undefined =
    integration.get('upsync_activities');
  integration.set({
    upsync_activities: {
      activity: false,
      donations: false,
      form_ids: [],
      people: false,
      ...current,
      [key]: value,
    },
  });
}

/** The staged attributes whose required option the save must check. */
const OPTION_ATTRIBUTES: ReadonlySet<string> = new Set([
  'activity_note_type',
  'fundraising',
  'upsync_activities',
]);

/**
 * Why the staged switches can't be saved: a switch is on without the option it needs. Only a save
 * that changes those settings is checked, so a saved state that lacks one can't block other edits.
 */
export function getMissingOptionReason(
  integration: VirtuousIntegration,
  t: TFunction,
): string | undefined {
  if (!integration.changedStagedAttributes.some((attribute) => OPTION_ATTRIBUTES.has(attribute))) {
    return undefined;
  }
  const upsync = integration.get('upsync_activities');
  if (upsync?.donations && !integration.get('fundraising')?.project_id) {
    return t('Select a project to sync donations to Virtuous.');
  }
  if (upsync?.activity && !integration.get('activity_note_type')) {
    return t('Select a note type to sync activity to Virtuous.');
  }
  return undefined;
}

/** `null` means the request failed, so every value is unavailable. */
export type TVirtuousSyncSummaryResult = IVirtuousSyncSummary | null;

function formatHealth(summary: TVirtuousSyncSummaryResult, t: TFunction): string {
  if (!summary || summary.is_healthy === null) {
    return UNAVAILABLE_VALUE;
  }
  return summary.is_healthy ? t('Healthy') : t('Needs attention');
}

/** The summary bar's stats, in display order. */
export function getSyncSummaryStats(
  summary: TVirtuousSyncSummaryResult,
  t: TFunction,
): IIntegrationUIStat[] {
  const needAttention = summary?.mappings_need_attention ?? 0;
  return [
    {
      key: 'last_synced',
      label: t('Last checked'),
      value: formatLastSynced(summary, t),
      icon: faClock,
    },
    {
      key: 'is_healthy',
      label: t('Connection'),
      value: formatHealth(summary, t),
      icon: faHeartPulse,
      attention: summary?.is_healthy === false,
    },
    {
      key: 'mappings_need_attention',
      label: t('Mappings need attention'),
      value: summary ? needAttention.toLocaleString() : UNAVAILABLE_VALUE,
      icon: faTriangleExclamation,
      attention: needAttention > 0,
    },
  ];
}
